import { pay } from '../../game/inventory';
import { api } from '../../ui/app/api';
import { Window } from '../../ui/app/Window';
import { Amount, Empty, TipLayer, Wallet, getGame, toast, useCtx } from '../economy/ui';
import { TRAVEL_FROM, type WaystoneInfo, travelCost } from './waystones';
import { allWaystones } from './world';

/** Pays the fare and jumps to the waystone's zone at its nearest spawn point. */
export function travelToWaystone(w: WaystoneInfo): boolean {
  const game = getGame();
  const p = game?.progression;
  if (!game || !p) return false;
  const kind = game.zone?.layout.kind ?? '';
  if (!TRAVEL_FROM.has(kind)) {
    toast('Waystones cannot be used from inside an instance.', '#e5484d');
    return false;
  }
  if (!p.char.waystones.includes(w.id)) return false;
  const cost = travelCost(p.char.level, w.zone === game.zone?.layout.id);
  if (!pay(p.char, p.account, { silver: cost })) {
    toast(`You need ${cost} silver to travel.`, '#e5484d');
    return false;
  }
  p.char.counters.waystoneTravels = (p.char.counters.waystoneTravels ?? 0) + 1;
  p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  game.travel(w.zone, w.spawn);
  return true;
}

/** Panel `waystone`: attuned waystones by zone; travel costs a little silver. */
export function WaystonePanel() {
  const ctx = useCtx();
  if (!ctx) return null;
  const { c, a } = ctx;
  const gameApi = api();
  const zoneId = gameApi.zoneId;
  const canLeave = TRAVEL_FROM.has(gameApi.zoneKind ?? '');
  const pos = gameApi.player?.pos;
  const all = allWaystones();
  const nearby = pos
    ? all.filter((w) => w.zone === zoneId && Math.hypot(w.x - pos.x, w.z - pos.z) < 14)
    : [];
  const hereId = nearby[0]?.id ?? null;
  const zones: {
    id: string;
    name: string;
    kind: string;
    levelRange: [number, number];
    list: WaystoneInfo[];
  }[] = [];
  for (const w of all) {
    let z = zones.find((x) => x.id === w.zone);
    if (!z)
      zones.push(
        (z = {
          id: w.zone,
          name: w.zoneName,
          kind: w.zoneKind,
          levelRange: w.levelRange,
          list: [],
        }),
      );
    z.list.push(w);
  }
  const attunedCount = all.filter((w) => c.waystones.includes(w.id)).length;
  return (
    <Window
      title="Waystones"
      width="min(640px, 96vw)"
      height="min(600px, 88vh)"
      className="fx-win fx-travel"
    >
      <div class="fx-bar-top">
        <span class="tv-loc">
          <span class="fx-h">Attuned</span> <b class="fx-num">{attunedCount}</b>
          <span class="fx-dim"> / {all.length}</span>
        </span>
        <span class="fx-spacer" />
        <Wallet a={a} c={c} />
      </div>
      <div class="tv-list fx-scroll">
        {zones.length === 0 && (
          <Empty title="No waystones known">Touch a waystone to attune to it.</Empty>
        )}
        {zones.map((z) => (
          <section key={z.id} class={`tv-zone${z.id === zoneId ? ' is-here' : ''}`}>
            <header class="tv-zone-h">
              <span class="tv-zone-n">{z.name}</span>
              <span class="tv-zone-l">
                {z.kind === 'town' ? 'City' : `Lv ${z.levelRange[0]}–${z.levelRange[1]}`}
              </span>
              {z.id === zoneId && <span class="tv-here">You are here</span>}
            </header>
            {z.list.map((w) => {
              const attuned = c.waystones.includes(w.id);
              const here = w.id === hereId;
              const cost = travelCost(c.level, w.zone === zoneId);
              const short = a.wallet.silver < cost;
              return (
                <div
                  key={w.id}
                  class={`tv-row${attuned ? '' : ' is-locked'}${here ? ' is-current' : ''}`}
                >
                  <span class="tv-stone" aria-hidden="true" />
                  <div class="tv-namecol">
                    <div class="tv-name">{w.name}</div>
                    <div class="fx-sub">
                      {!attuned
                        ? 'Not attuned — touch this waystone to add it'
                        : here
                          ? 'You are standing at this waystone'
                          : w.zone === zoneId
                            ? 'Same region · half fare'
                            : `Travel to ${w.zoneName}`}
                    </div>
                  </div>
                  {attuned && !here ? <Amount kind="silver" n={cost} bad={short} /> : <span />}
                  <button
                    class="btn btn--gold sm"
                    disabled={!attuned || here || !canLeave || short}
                    onClick={() => travelToWaystone(w)}
                  >
                    Travel
                  </button>
                </div>
              );
            })}
          </section>
        ))}
      </div>
      {!canLeave && <div class="tv-foot">Waystones can't be used from inside an instance.</div>}
      <TipLayer />
    </Window>
  );
}
